import PhotoGrayIcon from '@assets/images/common/image_dummy.svg';
import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { companySupportServiceApiClient } from '@src/api/company/supportService/supportServiceApiClient';
import { companySupportServiceTagApiClient } from '@src/api/company/supportService/supportServiceTagApiClient';
import { Pagination } from '@src/api/types/pagination';
import { SupportService } from '@src/api/types/supportService';
import { SupportServiceCategory } from '@src/api/types/supportServiceCategory';
import { SupportServiceTag } from '@src/api/types/supportServiceTag';
import PaginationComponent from '@src/components/Pagination';
import { useCategory } from '@src/context/CategoryContext';
import SupportServicesItem from '@src/features/company/support-services/components/SupportServicesItem';
import { useScrollMobile } from '@src/hooks/useScrollMobile';
import { COMPANY_URL } from '@src/routes/company';
import InputSearch from '@src/ui/elements/form/InputSearch';
import { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';

const FORM_CONSTANTS = {
  INITIAL_TITLE: 'すべての支援サービス',
  PER_PAGE: 5,
  // parseIntで文字列を10進数として解釈する
  INTEGER_RADIX: 10,
} as const;

function SupportServicesList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [pagination, setPagination] = useState<Pagination>();
  const [searchValue, setSearchValue] = useState('');
  const [searchedKeyword, setSearchedKeyword] = useState<string>('');
  const [supportServices, setSupportServices] = useState<SupportService[]>([]);
  const [supportServiceTags, setSupportServiceTags] = useState<SupportServiceTag[]>([]);
  const [caption, setCaption] = useState<string>('');
  const [tagsLoaded, setTagsLoaded] = useState(false);

  const { rawSupportServiceCategories, menuSupportServiceCategories, setSelectedCategorySlug } =
    useCategory();

  const { renderScrollMobile } = useScrollMobile({
    items: menuSupportServiceCategories,
  });

  const loadSupportServices = async (
    page = 1,
    keyword: string | null = null,
    category: SupportServiceCategory | null = null,
    tag: SupportServiceTag | null = null,
  ) => {
    const params: {
      page: number;
      title?: string;
      per_page: number;
      category_slug?: string;
      tag_slug?: string;
    } = {
      page,
      per_page: FORM_CONSTANTS.PER_PAGE,
    };
    if (category && category.slug) {
      params.category_slug = category.slug;
    } else if (tag && tag.slug) {
      params.tag_slug = tag.slug;
    } else if (keyword) {
      params.title = keyword;
    }

    const res = await companySupportServiceApiClient.index(params);
    handleApiResponse(
      res,
      (data) => {
        if (data.data.length === 0 && data.pagination.total > 0 && page > 1) {
          const query = new URLSearchParams(location.search);
          query.set('page', '1');
          navigate({ search: query.toString() }, { replace: true });
          return;
        }

        setSupportServices(data.data);
        setPagination(data.pagination);
        setSearchValue(params.title || '');
        setSearchedKeyword(params.title || '');
        setCaption(category?.name || tag?.name || keyword || '');
        setSelectedCategorySlug(category?.slug || '');
      },
      (error) => {
        console.error('Failed to load support services:', error);
      },
    );
  };

  useEffect(() => {
    const loadSupportServiceTags = async () => {
      const res = await companySupportServiceTagApiClient.index();
      handleApiResponse(res, (data) => {
        setSupportServiceTags(data);
        setTagsLoaded(true);
      });
    };

    loadSupportServiceTags();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!tagsLoaded) {
        return;
      }

      const query = new URLSearchParams(location.search);
      const page = parseInt(query.get('page') || '1', FORM_CONSTANTS.INTEGER_RADIX);
      const keyword = query.get('keyword');
      const categorySlug = query.get('category_slug') || '';
      const category = rawSupportServiceCategories.find((c) => c.slug === categorySlug) || null;
      const tagSlug = query.get('tag_slug') || '';
      const tag = supportServiceTags.find((t) => t.slug === tagSlug) || { id: 0, name: '' };
      await loadSupportServices(page, keyword, category, tag);
      window.scrollTo(0, 0);
    };

    fetchData();
  }, [location.search, tagsLoaded]);

  const handlePageChange = (page: number) => {
    const currentPage = new URLSearchParams(location.search).get('page');
    if (currentPage === page.toString()) {
      return;
    }
    const query = new URLSearchParams(location.search);
    query.set('page', page.toString());
    navigate({ search: query.toString() });
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const handleSearchSubmit = () => {
    const query = new URLSearchParams(location.search);
    const newKeyword = searchValue.trim();

    query.set('keyword', newKeyword);
    query.set('page', '1');
    query.delete('category_slug');
    query.delete('tag_slug');
    setCaption(`${newKeyword}`);
    navigate({ search: query.toString() });
  };

  const handleCaption = (caption: string) => {
    const filteredCategory = rawSupportServiceCategories.find((c) => c.name === caption);
    const filteredTag = supportServiceTags.find((t) => t.name === caption);
    const query = new URLSearchParams(location.search);
    const categorySlug = query.get('category_slug') || '';
    const tagSlug = query.get('tag_slug') || '';
    const keyword = query.get('keyword') || '';
    const category = rawSupportServiceCategories.find((c) => c.slug === categorySlug);
    const tag = supportServiceTags.find((t) => t.slug === tagSlug);

    if (supportServices.length > 0) {
      if (category) {
        return `${category.name}`;
      }
      if (tag) {
        return `${tag.name}`;
      }
      if (keyword) {
        return `${keyword} の検索結果`;
      }
      return FORM_CONSTANTS.INITIAL_TITLE;
    }

    if (!searchedKeyword && !filteredCategory && !filteredTag) {
      return '';
    }
    return `${caption}の検索結果：0件`;
  };

  return (
    <main className='px-6 pb-16 pt-10 lg:px-6 md:pt-6'>
      {/* ヘッダー部分 */}
      <div className='flex items-center justify-between lg:flex-col lg:items-start'>
        <h1 className='overflow-wrap-anywhere text-xl font-semibold lg:mt-6'>
          {handleCaption(caption)}
        </h1>
        <div className='mt-4 hidden max-w-full lg:-order-1 lg:block'>
          <div className='ml-[-24px] w-[calc(100%+48px)] pl-[24px]'>{renderScrollMobile()}</div>
        </div>
        <div className='w-[250px] min-w-[250px] lg:-order-2 lg:w-full'>
          <InputSearch
            value={searchValue}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            onSubmit={handleSearchSubmit}
            onClear={() => {
              setSearchValue('');
              navigate({ search: '' });
            }}
          />
        </div>
      </div>
      {/* 支援サービス一覧 */}
      <div className='mt-8 grid grid-cols-1 gap-10 md:mt-6'>
        {supportServices.length > 0 &&
          supportServices.map((supportService, index) => (
            <SupportServicesItem
              key={index}
              to={generatePath(COMPANY_URL.SUPPORT_SERVICES_SHOW, { id: supportService.id })}
              title={supportService.title}
              content={supportService.content}
              category={supportService.support_service_category.name}
              categorySlug={supportService.support_service_category.slug}
              tags={supportService.support_service_tags}
              thumbnail={supportService.thumbnail_url || PhotoGrayIcon}
            />
          ))}
      </div>

      {/* ページング */}
      {pagination && pagination.total > FORM_CONSTANTS.PER_PAGE && (
        <div className='mt-16 lg:flex lg:justify-center'>
          <PaginationComponent
            pagination={pagination}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </main>
  );
}

export default SupportServicesList;
